import React, {FC, useMemo, useState} from 'react'
import {Platform} from 'src/interfaces/Platform.interface'
import ConnectPlatformModal from './ConnectPlatformModal'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import useAppToast from 'src/hooks/useAppToast'
import {connectPlatformAsync, fetchConnectedPlatformAsync} from 'src/redux/actions/platformActions'
import {setStepsData} from 'src/redux/slices/tourPopupSlice'
import {
  buildConnectPlatformPayload,
  buildConnectionChannelState,
  resolveOnboardingConnectNature,
  validateConnectionRole,
} from 'src/utils/platformConnectionValidation'
import {getPlatformConnectionGuideUrl} from 'src/utils/platformConnectionGuideUrl'

type PlatformCardPropType = {
  data: Platform
  setSelectedPlatformId: React.Dispatch<React.SetStateAction<string | undefined>>
  onClickConnectTop: React.Dispatch<
    React.SetStateAction<{
      platformId: number | string | null
      isConnecting: boolean
    }>
  >
  isPrimary?: boolean
  setIsConnectedPlatformModal?: React.Dispatch<React.SetStateAction<boolean>>
}

const PlatformCard: FC<PlatformCardPropType> = ({
  data,
  onClickConnectTop,
  setSelectedPlatformId,
  isPrimary = false,
  setIsConnectedPlatformModal,
}) => {
  const {fetchedConnectedPlatformData, fetchedUserAccountData} = useAppSelector((state) => ({
    fetchedConnectedPlatformData: state.fetchConnectedPlatform.fetchedConnectedPlatformData,
    fetchedUserAccountData: state.userAccountData.fetchedUserAccountData,
  }))
  const {
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
  } = useAppSelector((state) => state.fetchConnectedPlatform)

  const [showConnectPlatformModal, setShowConnectPlatformModal] = useState<boolean>(false)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null)
  const [selectedConnectNature, setSelectedConnectNature] = useState<string>(
    isPrimary ? 'PRIMARY' : 'SECONDARY'
  )
  const {successToast, warningToast, errorToast} = useAppToast()
  const dispatch = useAppDispatch()

  const channelState = useMemo(
    () =>
      buildConnectionChannelState({
        mainStoreData,
        accountingStoreData,
        shippingStoreData,
        erpStoreData,
        omsStoreData,
        ticketStoreData,
        fetchedConnectedPlatformData,
      }),
    [
      mainStoreData,
      accountingStoreData,
      shippingStoreData,
      erpStoreData,
      omsStoreData,
      ticketStoreData,
      fetchedConnectedPlatformData,
    ]
  )

  const onboardingStep = isPrimary ? 'source' : 'destination'

  const openConnectModal = (platform: Platform, connectNature: string) => {
    setSelectedPlatform(platform)
    setSelectedConnectNature(connectNature)
    setShowConnectPlatformModal(true)
    onClickConnectTop({platformId: platform.id, isConnecting: true})
    setIsConnectedPlatformModal?.(false)
  }

  const handleConnectClick = (platform: Platform) => {
    const connectNature = resolveOnboardingConnectNature(platform, onboardingStep)
    const validation = validateConnectionRole(platform, connectNature, channelState)

    if (!validation.ok) {
      errorToast(validation.error ?? 'This platform cannot be connected in the selected role.')
      onClickConnectTop({platformId: null, isConnecting: false})
      return
    }

    openConnectModal(platform, connectNature)
  }

  const handleCloseModal = () => {
    setShowConnectPlatformModal(false)
    setSelectedPlatform(null)
    onClickConnectTop({platformId: null, isConnecting: false})
    setIsConnectedPlatformModal?.(true)
  }

  const handleDataReceived = (
    dataCollector: any,
    friendlyName: string,
    connection_type?: string,
    extras?: Record<string, any>
  ) => {
    if (dataCollector['friendlyName'] == '') {
      warningToast('Channel name is required')
      return
    }

    if (!selectedPlatform) return

    const existingSecondaryStore = fetchedConnectedPlatformData.find(
      (item) =>
        !item?.isMainStore &&
        !item?.isAccountingStore &&
        !item?.isShippingStore &&
        !item?.isErpStore &&
        !item?.isOmsStore &&
        !item?.isTicketStore
    )

    const payload = buildConnectPlatformPayload(
      selectedPlatform,
      dataCollector,
      friendlyName,
      selectedConnectNature,
      connection_type,
      {extras, channelState}
    )

    dispatch(connectPlatformAsync(payload))
      .then((response) => {
        dispatch(fetchConnectedPlatformAsync())
        if (response.meta.requestStatus === 'fulfilled') {
          const {id, name} = response.payload.data
          localStorage.setItem(`platformFirstConnect_${name}_${id.toString()}`, 'true')
          const statusCode = response.payload.status_code
          const platformName = payload.name

          if (
            response.payload?.data?.isMainStore ||
            response.payload?.data?.isAccountingStore ||
            response.payload?.data?.isShippingStore ||
            response.payload?.data?.isErpStore ||
            response.payload?.data?.isOmsStore
          ) {
            if (existingSecondaryStore) {
              dispatch(
                setStepsData({
                  stepData: {stepOne: 'finished', stepTwo: 'finished', stepThree: 'active'},
                  visible: true,
                })
              )
              if (existingSecondaryStore.id) {
                setSelectedPlatformId(existingSecondaryStore.id + '')
              }
            } else if (fetchedUserAccountData?.is_platform_choice_done) {
              if (fetchedConnectedPlatformData[0]?.id) {
                dispatch(
                  setStepsData({
                    stepData: {stepOne: 'finished', stepTwo: 'finished', stepThree: 'active'},
                    visible: true,
                  })
                )
                setSelectedPlatformId(fetchedConnectedPlatformData[0]?.id + '')
              }
            } else {
              dispatch(
                setStepsData({
                  stepData: {stepOne: 'finished', stepTwo: 'active', stepThree: 'inactive'},
                  visible: true,
                })
              )
            }
          } else {
            setSelectedPlatformId(response.payload?.data?.id)
            dispatch(
              setStepsData({
                stepData: {stepOne: 'finished', stepTwo: 'finished', stepThree: 'active'},
                visible: true,
              })
            )
          }

          if (response?.payload?.status_code == 201) {
            successToast(`${selectedPlatform.display_name} Channel Connected Successfully`)
            return
          }

          return errorToast(`Failed to connect platform ${platformName}. Status code: ${statusCode}`)
        }

        return errorToast('Failed to connect channel. ' + response?.payload?.data?.massage)
      })
      .catch(() => {
        errorToast('An error occurred while connecting the channel. Please try again.')
      })
  }

  return (
    <div
      className={
        showConnectPlatformModal
          ? 'col-xl-8 col-md-10 col-12 '
          : 'col-sm-6 col-lg-6 col-xl-4 col-md-6 m-0 mt-3'
      }
    >
      <div className='card position-relative mb-md-3 mb-2 mb-xl-8 cursor-pointer p-3'>
        {showConnectPlatformModal ? (
          <div className='d-flex justify-content-end px-4'>
            <a
              target='_blank'
              href={getPlatformConnectionGuideUrl(data?.name, data?.help_href)}
              className='btn btn-link text-primary fs-6 fw-bold '
              rel='noreferrer'
            >
              Click Here to See {data?.display_name} Connection Guide
            </a>
          </div>
        ) : null}
        <div className='align-items-center p-3 px-7 '>
          <div className='d-flex row'>
            <div className='col-xxl-9 col-md-8 col-9'>
              <div className='d-flex flex-column flex-grow-1 py-2 py-lg-9 me-2 w-100'>
                <div className=' fw-bolder fs-4 fs-md-2 mb-2'>{data.display_name}</div>
              </div>
            </div>
            <div className='col-xxl-3 col-md-4 col-3 d-flex justify-content-end'>
              <div className='w-100px h-50px'>
                <img
                  src={data.logo_ref}
                  alt={data.display_name}
                  style={{width: '100%', height: '100%', objectFit: 'contain'}}
                />
              </div>
            </div>
          </div>

          {showConnectPlatformModal ? (
            <ConnectPlatformModal
              isVisible={showConnectPlatformModal}
              closeModal={handleCloseModal}
              data={selectedPlatform}
              onDataReceived={handleDataReceived}
              connectNature={selectedConnectNature}
              isPrimary={selectedConnectNature === 'PRIMARY'}
            />
          ) : null}
        </div>
        {!showConnectPlatformModal && (
          <div className='d-flex justify-content-end mt-4'>
            <button
              className='btn btn-primary btn-sm me-2'
              onClick={() => {
                handleConnectClick(data)
              }}
            >
              Connect
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlatformCard
